import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

const categoryIcons: Record<string, string> = {
  shoes: 'ri-footprint-line',
  hoodies: 'ri-shirt-line',
  tshirts: 'ri-t-shirt-line',
  jackets: 'ri-windy-line',
  pants: 'ri-pantone-line',
  headwear: 'ri-sun-line',
  sets: 'ri-stack-line',
  underwear: 'ri-checkbox-blank-circle-line',
  jersey: 'ri-team-line',
  accessories: 'ri-vip-crown-line',
  bags: 'ri-shopping-bag-3-line',
  belts: 'ri-link',
  perfumes: 'ri-flask-line',
  other: 'ri-more-2-line',
};

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCategorySelect = (url: string) => {
    setIsDropdownOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-[0_1px_3px_rgba(0,0,0,0.04)]'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          <div className="flex items-center gap-8">
            <Link
              to="/"
              className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-store-2-line text-background-50 text-sm"></i>
              </div>
              <span className="font-heading text-lg font-bold text-foreground-900 tracking-tight hidden sm:block">
                kakobuyfinds
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-6">
              <a href="#categories" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">
                Categories
              </a>
              <a href="#guide" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">
                Guide
              </a>
              <a href="#articles" className="text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors whitespace-nowrap cursor-pointer">
                Articles
              </a>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2" ref={dropdownRef}>
              <div className="relative">
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 bg-background-100 hover:bg-background-200 rounded-full text-sm font-medium text-foreground-700 transition-all cursor-pointer whitespace-nowrap"
                >
                  <span>Browse Categories</span>
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isDropdownOpen && (
                  <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.08)] border border-background-200/60 py-2 max-h-80 overflow-y-auto animate-scale-in z-50">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => handleCategorySelect(cat.url)}
                        className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors flex items-center justify-between cursor-pointer whitespace-nowrap"
                      >
                        <span>{cat.name}</span>
                        <i className="ri-external-link-line text-xs text-foreground-400"></i>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-2 bg-foreground-900 text-background-50 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap"
              >
                Kakobuy Sheet
                <i className="ri-arrow-right-up-line ml-1 text-xs"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
              >
                Buy It Now
                <i className="ri-shopping-bag-line ml-1 text-xs"></i>
              </a>
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 cursor-pointer"
            >
              <i className={`text-lg text-foreground-700 ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/60 mt-2 animate-scale-in">
            <div className="py-3 max-h-[calc(100vh-4rem)] overflow-y-auto">
              <div className="flex flex-col gap-1.5 mb-4">
                <a
                  href="#categories"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-7 h-7 flex items-center justify-center bg-background-100 rounded-lg">
                    <i className="ri-grid-line text-sm text-foreground-500"></i>
                  </div>
                  Categories
                </a>
                <a
                  href="#guide"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-7 h-7 flex items-center justify-center bg-background-100 rounded-lg">
                    <i className="ri-book-open-line text-sm text-foreground-500"></i>
                  </div>
                  Guide
                </a>
                <a
                  href="#articles"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-7 h-7 flex items-center justify-center bg-background-100 rounded-lg">
                    <i className="ri-article-line text-sm text-foreground-500"></i>
                  </div>
                  Articles
                </a>
              </div>

              <div className="mb-3">
                <p className="px-3 text-[11px] font-semibold text-foreground-400 uppercase tracking-widest mb-2">
                  Browse 14 Categories
                </p>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      window.open(cat.url, '_blank', 'noopener,noreferrer');
                    }}
                    className="group flex flex-col items-start gap-1.5 p-3 bg-background-100 hover:bg-background-200 rounded-xl transition-all cursor-pointer text-left"
                  >
                    <div className="flex items-center gap-2 w-full">
                      <div className="w-7 h-7 flex items-center justify-center bg-background-50 rounded-lg group-hover:bg-primary-50 transition-colors flex-shrink-0">
                        <i className={`${categoryIcons[cat.id] || 'ri-price-tag-3-line'} text-xs text-foreground-500 group-hover:text-primary-600 transition-colors`}></i>
                      </div>
                      <span className="text-xs font-semibold text-foreground-800 truncate flex-1 whitespace-nowrap">
                        {cat.name}
                      </span>
                    </div>
                    <span className="text-[10px] text-foreground-400 pl-9 whitespace-nowrap">
                      {cat.itemCount}
                    </span>
                  </button>
                ))}
              </div>

              <div className="flex flex-col gap-2 px-1">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full px-4 py-3 bg-foreground-900 text-background-50 rounded-full text-sm font-semibold text-center hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-store-2-line text-sm"></i>
                  Kakobuy Sheet
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full px-4 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold text-center hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                >
                  <i className="ri-shopping-bag-line text-sm"></i>
                  Buy It Now
                  <i className="ri-arrow-right-up-line text-xs"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}